import React, {useEffect, useState} from "react";
import {Link, useParams} from "react-router-dom";
import {apiUrl, getNtsSheet} from "../../utils/api";
import "./TrailDetails.css";
import "./NtsSheet.css";

const PRODUCTS = [
    {
        id: "cantopo",
        title: "CanTopo",
        blurb: "The current map, drawn from NRCan's topographic database.",
    },
    {
        id: "canmatrix",
        title: "CanMatrix",
        blurb: "A scan of the older printed paper map.",
    },
];

const FORMATS = {
    geopdf: {label: "GeoPDF", use: "For Avenza and other map apps that show your position"},
    geotiff: {label: "GeoTIFF", use: "For GIS and GPS mapping software"},
    pdf: {label: "PDF", use: "For viewing and printing"},
    tif: {label: "TIFF", use: "Plain image, for printing"},
    prtpdf: {label: "Print PDF", use: "For printing at full size"},
    prttif: {label: "Print TIFF", use: "Plain image, for printing at full size"},
};

const FORMAT_ORDER = ["geopdf", "geotiff", "pdf", "prtpdf", "tif", "prttif"];

// "092g14" -> "092G14"
function sheetName(sheet) {
    return sheet.toUpperCase();
}

function FileRow({file}) {
    const format = FORMATS[file.format] || {label: file.format.toUpperCase(), use: ""};
    return <li className="nts-file">
        <div>
            <span className="nts-file-format">{format.label}</span>
            {format.use && <span className="nts-file-use">{format.use}</span>}
        </div>
        <a className="nts-file-download" href={apiUrl(file.url)} download={file.name}>
            Download <span>{file.size}B zip</span>
        </a>
    </li>;
}

export default function NtsSheet() {
    const {sheet} = useParams();
    const [state, setState] = useState({status: "loading"});

    useEffect(() => {
        const controller = new AbortController();
        setState({status: "loading"});
        getNtsSheet(sheet.toLowerCase(), controller.signal)
            .then((data) => setState({status: "ready", data}))
            .catch((e) => {
                if (e.name !== "AbortError") setState({status: "missing"});
            });
        return () => controller.abort();
    }, [sheet]);

    if (state.status === "loading") {
        return <div className="trail-page"><p className="trail-message">Loading map sheet…</p></div>;
    }
    if (state.status === "missing") {
        return <div className="trail-page">
            <p className="trail-message">
                There are no downloads for NTS map {sheetName(sheet)}. Natural Resources Canada hasn't
                published this sheet, or it couldn't be reached. <Link to="/trails">Browse trails</Link> instead.
            </p>
        </div>;
    }

    const {data} = state;
    const products = PRODUCTS
        .map((product) => ({
            ...product,
            files: data.files
                .filter((file) => file.product === product.id)
                .sort((a, b) => FORMAT_ORDER.indexOf(a.format) - FORMAT_ORDER.indexOf(b.format)),
        }))
        .filter((product) => product.files.length > 0);

    return <article className="trail-page nts-page">
        <header>
            <p className="nts-kicker">NTS 1:50,000 topographic map</p>
            <h1>{sheetName(data.sheet)}</h1>
            <p className="nts-intro">
                Free map downloads from Natural Resources Canada. Each one is a zip file.
                The first download of a file takes a little longer while we fetch it from NRCan.
            </p>
        </header>

        {products.map((product) => <section key={product.id} className="nts-product">
            <h2>{product.title}</h2>
            <p>{product.blurb}</p>
            <ul>
                {product.files.map((file) => <FileRow key={file.name} file={file}/>)}
            </ul>
        </section>)}

        <p className="trail-source">
            Maps © Natural Resources Canada, from <a href={data.source_url} target="_blank" rel="noreferrer">ftp.maps.canada.ca</a>
        </p>
    </article>;
}
